'use client';

import { TriangleAlert } from 'lucide-react';
import RecentHighRiskIssues from '~/app/(secured)/[workspaceId]/content-scanner/_components/Dashboard/RecentHighRiskIssues';
import Chip from '~/components/Chip';
import Skeleton from '~/components/Loading/Skeleton';
import { cn } from '~/lib/utils';
import { api, skipBatchOption } from '~/utils/api';

type HomeHighRiskIssuesProps = {
  enabled: boolean;
  hideHeader?: boolean;
  /** Total-count chip. Off when the parent tab already shows the same total. */
  showCount?: boolean;
};

export default function HomeHighRiskIssues({
  enabled,
  hideHeader,
  showCount = true,
}: HomeHighRiskIssuesProps) {
  // Matches the compact list query so React Query dedupes the request.
  const { data: highRiskResponse, isLoading } = api.scanner.getHighRiskIssues.useQuery(
    { page: 1, limit: 5 },
    { ...skipBatchOption, enabled },
  );

  if (!enabled) return null;

  const totalCount = highRiskResponse?.totalCount ?? 0;
  const hasIssues = totalCount > 0;

  const body = (
    <div
      className={cn(
        'bg-card overflow-hidden rounded-xl border',
        hasIssues
          ? 'border-destructive/40 shadow-[0_1px_2px_rgba(239,68,68,0.10)]'
          : 'border-border',
      )}
    >
      <RecentHighRiskIssues compact limit={5} showHeader={false} />
    </div>
  );

  if (hideHeader) return body;

  return (
    <section aria-labelledby="home-high-risk-heading">
      <div className={cn('mb-2 flex gap-2', showCount ? 'items-center' : 'items-baseline')}>
        <h2
          id="home-high-risk-heading"
          className="text-foreground flex items-center gap-1.5 text-sm font-semibold tracking-tight"
        >
          <TriangleAlert
            aria-hidden
            className={cn(
              'size-3.5 shrink-0',
              hasIssues ? 'text-destructive' : 'text-muted-foreground/70',
            )}
          />
          High-Risk Issues
        </h2>
        {showCount ? (
          isLoading ? (
            <Skeleton className="h-5 min-w-[1.375rem] rounded-md" />
          ) : (
            <Chip
              level={hasIssues ? 'alert' : 'neutral'}
              text={String(totalCount)}
              className="h-5 min-w-[1.375rem] justify-center px-1.5 tabular-nums"
            />
          )
        ) : null}
      </div>
      {body}
    </section>
  );
}
